import React, { useState } from 'react'
import { Handle, Position, NodeProps } from 'reactflow'
import { CheckSquare, Settings } from 'lucide-react'

interface TaskNodeData {
  label: string
  config: {
    description: string
    expectedOutput: string
    agent: string | null
  }
}

const TaskNode: React.FC<NodeProps<TaskNodeData>> = ({ data, selected }) => {
  const [showConfig, setShowConfig] = useState(false)

  return (
    <>
      <div className={`node-base node-task w-48 h-20 ${selected ? 'ring-2' : ''}`} style={{borderColor: selected ? 'var(--color-node-task)' : undefined}}>
        <div className="flex items-center h-full p-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3" style={{backgroundColor: 'var(--color-node-task-bg)'}}>
            <CheckSquare className="w-5 h-5" style={{color: 'var(--color-node-task)'}} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900 dark:text-white text-sm truncate">
              {data.label}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {data.config.description}
            </div>
          </div>
          <button
            onClick={() => setShowConfig(true)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
        
        {/* Input handle */}
        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 bg-white border-2 border-purple-600"
        />
        
        {/* Output handle */}
        <Handle
          type="source"
          position={Position.Right}
          className="w-3 h-3 bg-white border-2 border-purple-600"
        />
      </div>
      
      {/* Configuration Modal */}
      {showConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Configure Task
              </h3>
              <button
                onClick={() => setShowConfig(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Task Description
                </label>
                <textarea
                  value={data.config.description}
                  className="input h-24 resize-none"
                  placeholder="Describe what this task should accomplish"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expected Output
                </label>
                <textarea
                  value={data.config.expectedOutput}
                  className="input h-20 resize-none"
                  placeholder="What should be the output of this task?"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Assigned Agent
                </label>
                <select value={data.config.agent || ''} className="input">
                  <option value="">Select an agent...</option>
                  <option value="research-agent">Research Agent</option>
                  <option value="writer-agent">Writer Agent</option>
                  <option value="analyst-agent">Analyst Agent</option>
                </select>
              </div>
              
              <div className="flex justify-end space-x-2 pt-4">
                <button
                  onClick={() => setShowConfig(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowConfig(false)}
                  className="btn btn-primary"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default TaskNode
